import { includes } from 'lodash'
import {
  BUTTON_ROLE,
  ICON_BUTTON_ROLE,
  STYLABLE_BUTTON_ROLE,
} from '@wix/wix-data-client-common-standalone'

import { Breadcrumb, Logger } from '../logger'
import {
  isReadOnly,
  isEditable,
  selectNextDynamicPageUrl,
  selectPreviousDynamicPageUrl,
} from '../dataset-controller/rootReducer'
import {
  canLoadMoreItems,
  hasNextItem,
  hasPreviousItem,
  hasNextPage,
  hasPreviousPage,
} from '../helpers/paginationUtils'
import { appContext } from '../viewer-app-module/DataBindingAppContext'
import { AdaptedComponent } from '../inverted-dependencies/components/componentFactory'

import { StateSideEffectSubscriberFactory } from './types'
import { findComponentWithAction, getUniqueDisableableComponents } from './utils'

export const syncLinkableComponentsWithStateFactory: StateSideEffectSubscriberFactory = ({
  store: { getState },
  logger,
  recordStore,
  datasetId,
  components,
  randomSort,
}) => {
  const shouldEnableLinkedComponent: Record<string, () => boolean> = {
    new: () => !isReadOnly(getState()) && !randomSort,
    save: () => isEditable(getState()),
    revert: () => isEditable(getState()),
    remove: () => isEditable(getState()),
    next: () =>
      hasNextItem({
        state: getState(),
        recordStore,
      }),
    previous: () => hasPreviousItem({ state: getState() }),
    nextPage: () =>
      hasNextPage({
        state: getState(),
        recordStore,
      }),
    previousPage: () => hasPreviousPage({ state: getState() }),
    nextDynamicPage: () => selectNextDynamicPageUrl(getState()).hasUrl(),
    previousDynamicPage: () =>
      selectPreviousDynamicPageUrl(getState()).hasUrl(),
    loadMore: () =>
      canLoadMoreItems({
        state: getState(),
        recordStore,
      }),
  }

  const linkedComponents = getDisableableLinkedComponents(
    components,
    Object.keys(shouldEnableLinkedComponent),
  )

  if (linkedComponents.length === 0) {
    return
  }

  return () => {
    linkedComponents.forEach(component => {
      const action = component.connectionConfig?.events?.onClick?.action

      if (!action || !shouldEnableLinkedComponent[action]) {
        return
      }

      const shouldBeEnabled = shouldEnableLinkedComponent[action]()
      updateLinkedComponentEnabledState(component, shouldBeEnabled, logger, datasetId, linkedComponents)
    })
  }
}

const LINKABLE_DISABLEABLE_COMPONENTS = [
  BUTTON_ROLE,
  ICON_BUTTON_ROLE,
  STYLABLE_BUTTON_ROLE,
]

const getDisableableLinkedComponents = (components: AdaptedComponent[], disableableActions: string[]) =>
  getUniqueDisableableComponents(components)
    .filter(({ role }) => includes(LINKABLE_DISABLEABLE_COMPONENTS, role))
    .filter(({ connectionConfig }) =>
      includes(
        disableableActions,
        connectionConfig?.events?.onClick?.action,
      ),
    )

const oppositeFocusableActions: Record<string, string> = {
  next: 'previous',
  previous: 'next',
  nextPage: 'previousPage',
  previousPage: 'nextPage',
}

const updateLinkedComponentEnabledState = (
  component: AdaptedComponent,
  shouldBeEnabled: boolean,
  logger: Logger,
  datasetId: string,
  linkedComponents: AdaptedComponent[],
) => {
  if (component.enabled === shouldBeEnabled) {
    return
  }

  if (shouldBeEnabled) {
    component.enable()
  } else {
    component.disable()

    const action = component.connectionConfig?.events?.onClick?.action
    const lastActionInfo = appContext.lastAction.get()

    if (lastActionInfo?.datasetId === datasetId && lastActionInfo?.action === action && oppositeFocusableActions[action]) {
      const oppositeComponent = findComponentWithAction(linkedComponents, oppositeFocusableActions[action])

      if (oppositeComponent) {
        oppositeComponent.focus()
        appContext.lastAction.clear()
      }
    }
  }

  logger.log(
    new Breadcrumb({
      category: 'components',
      message: `${component.id} changed to ${shouldBeEnabled ? 'enabled' : 'disabled'} (dataset: ${datasetId})`,
    }),
  )
}
